import { fetchAdminGameAnalytics } from './adminAnalyticsService';
import { monthKey, monthLabel, monthRange } from './adminStatsService';
import { EUR_PER_USD, toUsd } from '../utils/currency';
import { isDatabetrixGameId, isDatabetrixProvider } from '../utils/gameProviders';

const CURRENCY_ORDER = ['USD', 'LBP', 'TND', 'TRY', 'IQD', 'KWD'];

/**
 * Each provider bills its rate on its net GGR summed over every currency.
 * Databetrix and Betinovio bill their own rate on every studio they carry
 * (Evolution and Pragmatic Play respectively), so only rounds played through
 * Eva fall under the Eva rates.
 *
 * Eva studio percentages are the Slots rate / Live rate columns on Eva's
 * August 2026 Bluebetlive royalty invoice. Where a studio also lists a
 * branded or premium rate, the general rate covers nearly all of the GGR.
 * Belatra, BGaming and Galaxsys are 0% on that invoice. Studios it does not
 * list stay on the old 9% estimate.
 * Pragmatic Play is the one studio whose slots and live rates differ (9% / 12%).
 * InOut premium titles are 9% there and general titles 8%; rounds are billed
 * at 8% because the feed does not flag premium games.
 */
const evaStudio = (id, label, providers, rate, liveRate) => ({
  id: `eva-${id}`,
  label: `Eva · ${label}`,
  product: 'casino',
  rate,
  ...(liveRate != null && liveRate !== rate ? { liveRate } : {}),
  providers
});

const EVA_STUDIOS = [
  evaStudio('pragmatic', 'Pragmatic Play', ['pragmatic'], 0.09, 0.12),
  evaStudio('evolution', 'Evolution', ['evo-evolution', 'evo-live'], 0.12),
  evaStudio('netent', 'NetEnt', ['evo-netent', 'netent'], 0.11),
  evaStudio('redtiger', 'Red Tiger', ['evo-redtiger', 'redtiger', 'red tiger'], 0.11),
  evaStudio('playson', 'Playson', ['playson'], 0.11),
  evaStudio('amusnet', 'Amusnet', ['amusnet'], 0.14),
  evaStudio('booming', 'Booming Games', ['booming', 'booming games'], 0.1),
  evaStudio('worldmatch', 'WorldMatch', ['worldmatch'], 0.12),
  evaStudio('creedroomz', 'CreedRoomz', ['creedroomz'], 0.11),
  evaStudio('hacksaw', 'Hacksaw', ['hacksaw'], 0.1),
  evaStudio('pateplay', 'Pateplay', ['pateplay'], 0.09),
  evaStudio('fazi', 'Fazi', ['fazi'], 0.09),
  evaStudio('mascot', 'Mascot', ['mascot', 'mascot gaming'], 0.09),
  evaStudio('popok', 'PopOK', ['popok', 'popok gaming'], 0.085),
  evaStudio('pascal', 'Pascal', ['pascal-gaming', 'pascal', 'pascal gaming'], 0.085),
  evaStudio('inout', 'InOut', ['inout', 'inout games'], 0.08),
  evaStudio('belatra', 'Belatra', ['belatragames', 'belatra'], 0),
  evaStudio('bgaming', 'BGaming', ['bgaming'], 0),
  evaStudio('galaxsys', 'Galaxsys', ['digitain-galaxsys', 'galaxsys', 'digitain/galaxsys'], 0)
];

const EVA_LINE_BY_PROVIDER = new Map(
  EVA_STUDIOS.flatMap((studio) => studio.providers.map((provider) => [provider, studio.id]))
);

export const BILL_LINES = [
  {
    id: 'betconstruct',
    label: 'BetConstruct',
    product: 'sports',
    rate: 0.11,
    // The new price is the same rate on a new GGR: each listed currency's GGR
    // divided by its factor, every other currency's GGR counted in full.
    // The 30% is not a rate change. It is an error range added on top of that
    // reduced bill.
    newPriceDivisors: { LBP: 19, TND: 16, USD: 43 },
    errorRange: 0.3
  },
  { id: 'gr8', label: 'GR8', product: 'sports', rate: 0.085 },
  { id: 'betinovio', label: 'Betinovio', product: 'casino', rate: 0.04 },
  { id: 'databetrix', label: 'Databetrix', product: 'casino', rate: 0.07 },
  ...EVA_STUDIOS.map(({ providers, ...line }) => line),
  { id: 'eva-other', label: 'Eva · other studios', product: 'casino', rate: 0.09, estimate: true }
];

// Credits the wallet books on the casino platform that are not a provider's round.
const NON_GAME_IDS = new Set(['weekly_ggr_bonus']);

// Pragmatic live tables use short numeric game ids. These numeric ids are crash
// or bingo titles on the same scheme, and Eva bills them on the slots rate.
const PRAGMATIC_NOT_LIVE = new Set(['1301', '1320', '2201', '1701', '1501', '2750', '2101', '4001']);

const isPragmaticLiveGame = (gameId) => {
  const id = String(gameId || '');
  return /^\d{2,5}$/.test(id) && !PRAGMATIC_NOT_LIVE.has(id);
};

const evaLineFor = (provider) => EVA_LINE_BY_PROVIDER.get(String(provider || '').toLowerCase()) || 'eva-other';

const isLiveGgr = (lineId, gameId) => lineId === 'eva-pragmatic' && isPragmaticLiveGame(gameId);

const casinoLineFor = ({ gameId, provider }) => (
  isDatabetrixGameId(gameId) || isDatabetrixProvider(provider) ? 'databetrix' : evaLineFor(provider)
);

const createBucket = () => ({ byCurrency: new Map(), byStudio: new Map(), slotsGgrUsd: 0, liveGgrUsd: 0 });

const addTo = (bucket, { currency, provider, ggr, turnover, live = false }) => {
  const byCurrency = bucket.byCurrency.get(currency) || { currency, ggr: 0, turnover: 0 };
  byCurrency.ggr += ggr;
  byCurrency.turnover += turnover;
  bucket.byCurrency.set(currency, byCurrency);

  const ggrUsd = toUsd(ggr, currency);
  if (live) bucket.liveGgrUsd += ggrUsd;
  else bucket.slotsGgrUsd += ggrUsd;

  if (!provider) return;
  const byStudio = bucket.byStudio.get(provider) || { provider, ggrUsd: 0, turnoverUsd: 0 };
  byStudio.ggrUsd += ggrUsd;
  byStudio.turnoverUsd += toUsd(turnover, currency);
  bucket.byStudio.set(provider, byStudio);
};

// Moving Betinovio out of the Eva buckets leaves float residue where the two cancel.
const significant = (...values) => values.some((value) => Math.abs(value) > 1e-6);
const sumBy = (rows, key) => rows.reduce((total, row) => total + (row[key] || 0), 0);

const newPriceOf = (divisors, rate, currencies, errorRange = 0) => {
  const rows = currencies.map((row) => {
    const divisor = divisors[row.currency] || 1;
    const ggr = row.ggr / divisor;
    return { currency: row.currency, divisor, divided: divisor !== 1, ggr, ggrUsd: toUsd(ggr, row.currency) };
  });
  const ggrUsd = sumBy(rows, 'ggrUsd');
  const totalUsd = ggrUsd * rate;
  const withErrorUsd = totalUsd > 0 ? totalUsd * (1 + errorRange) : totalUsd;
  return {
    currencies: rows,
    ggrUsd,
    totalUsd,
    totalEur: totalUsd * EUR_PER_USD,
    errorRange,
    withErrorUsd,
    withErrorEur: withErrorUsd * EUR_PER_USD
  };
};

const finalize = (definition, bucket) => {
  const currencies = [...bucket.byCurrency.values()]
    .filter((row) => significant(row.ggr, row.turnover))
    .map((row) => ({ ...row, ggrUsd: toUsd(row.ggr, row.currency), turnoverUsd: toUsd(row.turnover, row.currency) }))
    .sort((a, b) => CURRENCY_ORDER.indexOf(a.currency) - CURRENCY_ORDER.indexOf(b.currency));
  const studios = [...bucket.byStudio.values()]
    .filter((row) => significant(row.ggrUsd, row.turnoverUsd))
    .sort((a, b) => b.ggrUsd - a.ggrUsd);
  const ggrUsd = sumBy(currencies, 'ggrUsd');
  const slotsRate = definition.rate;
  const liveRate = definition.liveRate ?? definition.rate;
  const billUsd = bucket.slotsGgrUsd * slotsRate + bucket.liveGgrUsd * liveRate;
  return {
    ...definition,
    currencies,
    studios,
    ggrUsd,
    slotsGgrUsd: bucket.slotsGgrUsd,
    liveGgrUsd: bucket.liveGgrUsd,
    turnoverUsd: sumBy(currencies, 'turnoverUsd'),
    billUsd,
    billEur: billUsd * EUR_PER_USD,
    newPrice: definition.newPriceDivisors
      ? newPriceOf(definition.newPriceDivisors, definition.rate, currencies, definition.errorRange || 0)
      : null
  };
};

/**
 * Betinovio carries Pragmatic titles under the same game ids and provider as
 * Eva, so only the per-integration aggregates can separate them, and only as
 * far as their own ingest has reached.
 */
const betinovioCoverage = (response, start, end) => {
  const through = Number(response?.ingest?.lastTsIntegration) || 0;
  if (!Array.isArray(response?.casino?.integrations) || through < start.getTime()) {
    return { status: 'unavailable', through: through || null };
  }
  const needed = Math.min(end.getTime(), Number(response?.ingest?.lastTsPm8) || end.getTime());
  return { status: through >= needed ? 'complete' : 'partial', through };
};

export const computeProviderBill = (response, { year, month }) => {
  const { start, end } = monthRange(year, month);
  const buckets = Object.fromEntries(BILL_LINES.map((line) => [line.id, createBucket()]));
  const excluded = createBucket();

  (response?.sports?.books || []).forEach((row) => {
    if (!buckets[row.book]) return;
    addTo(buckets[row.book], {
      currency: row.currency,
      ggr: Number(row.ggr) || 0,
      turnover: Number(row.betAmount) || 0
    });
  });

  (response?.casino?.games || []).forEach((row) => {
    const provider = row.provider || 'unknown';
    const lineId = casinoLineFor({ gameId: row.gameId, provider });
    const entry = {
      currency: row.currency,
      provider,
      ggr: Number(row.ggr) || 0,
      turnover: Number(row.betAmount) || 0,
      live: isLiveGgr(lineId, row.gameId)
    };
    addTo(NON_GAME_IDS.has(row.gameId) ? excluded : buckets[lineId], entry);
  });

  const betinovio = betinovioCoverage(response, start, end);
  if (betinovio.status !== 'unavailable') {
    response.casino.integrations
      .filter((row) => row.integration === 'betinovio')
      .forEach((row) => {
        const provider = row.provider || 'pragmatic';
        const ggr = Number(row.ggr) || 0;
        const turnover = Number(row.betAmount) || 0;
        // The integration aggregate has no game id, so the share comes out of the
        // slots bucket. Pragmatic's live rate only applies to game rows that do.
        addTo(buckets[evaLineFor(provider)], { currency: row.currency, provider, ggr: -ggr, turnover: -turnover });
        addTo(buckets.betinovio, { currency: row.currency, provider, ggr, turnover });
      });
  }

  const lines = BILL_LINES.map((definition) => finalize(definition, buckets[definition.id]));
  // Providers bill separately, so a line with negative GGR owes nothing and
  // cannot offset what another provider bills.
  const totalOf = (amountOf) => lines.reduce((total, line) => total + Math.max(0, amountOf(line)), 0);
  const totalUsd = totalOf((line) => line.billUsd);
  const newPriceTotalUsd = totalOf((line) => (line.newPrice ? line.newPrice.totalUsd : line.billUsd));
  const newPriceWithErrorTotalUsd = totalOf((line) => (
    line.newPrice ? line.newPrice.withErrorUsd : line.billUsd
  ));
  const notBilled = finalize({ id: 'weekly-bonus', label: 'Weekly GGR bonus', rate: 0 }, excluded);

  return {
    monthKey: monthKey(year, month),
    label: monthLabel(year, month),
    startDate: start,
    endDate: end,
    lines,
    totalUsd,
    totalEur: totalUsd * EUR_PER_USD,
    newPriceTotalUsd,
    newPriceTotalEur: newPriceTotalUsd * EUR_PER_USD,
    newPriceWithErrorTotalUsd,
    newPriceWithErrorTotalEur: newPriceWithErrorTotalUsd * EUR_PER_USD,
    excluded: notBilled.currencies.length ? notBilled : null,
    betinovio,
    ingest: response?.ingest || null
  };
};

export const fetchProviderBill = async ({ year, month }) => {
  const { start, end } = monthRange(year, month);
  const response = await fetchAdminGameAnalytics({
    startDate: start,
    endDate: end,
    currency: null,
    tab: 'overview'
  });
  return computeProviderBill(response, { year, month });
};
